import { z } from 'zod';
import {
  currencySchema,
  isoTimestampSchema,
  localeSchema,
  timeZoneSchema,
  uuidSchema,
} from './common';

export const signInRequestSchema = z.object({
  email: z
    .email()
    .max(254)
    .transform((e) => e.toLowerCase()),
  password: z.string().min(1).max(256),
  /** Optional tenant slug when the user belongs to several tenants. */
  tenant_slug: z.string().max(64).optional(),
  /** Required (401 MFA_REQUIRED otherwise) when the account has TOTP enabled (ADR-0031). */
  totp_code: z.string().trim().max(10).optional(),
});
export type SignInRequest = z.input<typeof signInRequestSchema>;

export const switchTenantRequestSchema = z.object({ tenant_id: uuidSchema });

export const branchSchema = z.object({
  id: uuidSchema,
  tenant_id: uuidSchema,
  brand_id: uuidSchema.nullable(),
  code: z.string(),
  name: z.string(),
  time_zone: timeZoneSchema,
  currency: currencySchema,
  locale: localeSchema,
  status: z.enum(['active', 'inactive']),
  tax_rate_bps: z.number().int(),
  service_charge_bps: z.number().int(),
  rounding_unit_minor: z.number().int(),
  /** Waste valued at or above this needs approval by a second person; 0 = always. */
  waste_approval_threshold_minor: z.number().int(),
  /** Location sold dishes draw ingredients from; null = kitchen location, else default. */
  consumption_location_id: uuidSchema.nullable().optional(),
  /** On: who recorded/counted/requested cannot decide; off: may, with a reason (ADR-0037). */
  separation_of_duties: z.boolean(),
  version: z.number().int(),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
});
export type Branch = z.infer<typeof branchSchema>;

export const createBranchRequestSchema = z.object({
  code: z
    .string()
    .min(2)
    .max(16)
    .regex(/^[A-Z0-9-]+$/, 'Upper-case letters, digits and dashes only'),
  name: z.string().trim().min(1).max(120),
  brand_id: uuidSchema.nullable().default(null),
  time_zone: timeZoneSchema.default('Asia/Jakarta'),
  currency: currencySchema.default('IDR'),
  locale: localeSchema.default('id'),
});
export type CreateBranchRequest = z.input<typeof createBranchRequestSchema>;

export const membershipSummarySchema = z.object({
  tenant_id: uuidSchema,
  tenant_name: z.string(),
  tenant_slug: z.string(),
});

export const meResponseSchema = z.object({
  user: z.object({
    id: uuidSchema,
    email: z.string(),
    display_name: z.string(),
    locale: localeSchema,
  }),
  active_tenant: membershipSummarySchema.nullable(),
  /** Plan and lifecycle of the active tenant (ADR-0030); the UI hides modules outside the plan. */
  subscription: z
    .object({
      plan_code: z.string(),
      plan_name: z.string(),
      status: z.enum(['trialing', 'active', 'past_due', 'read_only', 'suspended', 'cancelled']),
      trial_ends_at: z.string().nullable(),
      current_period_end: z.string().nullable(),
      modules: z.array(z.string()),
      limits: z.object({
        max_branches: z.number().int().nullable(),
        max_staff: z.number().int().nullable(),
        max_devices: z.number().int().nullable(),
      }),
    })
    .nullable()
    .optional(),
  /** Set when the user is a platform operator (ADR-0030); never a tenant permission. */
  platform_role: z.enum(['owner', 'operator', 'support']).nullable().optional(),
  memberships: z.array(membershipSummarySchema),
  branches: z.array(
    z.object({
      id: uuidSchema,
      code: z.string(),
      name: z.string(),
      time_zone: timeZoneSchema,
      currency: currencySchema,
      locale: localeSchema,
      /** Branch setting (ADR-0037); absent = treat as on (strict). */
      separation_of_duties: z.boolean().optional(),
      permissions: z.array(z.string()),
    }),
  ),
  tenant_permissions: z.array(z.string()),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const branchListResponseSchema = z.object({ items: z.array(branchSchema) });
